import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import QRCode from "qrcode.react";
import "../styles/global.css";
import logo from "../assets/logo.png"; // Make sure your logo exists

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [balance, setBalance] = useState("--");
  const [username, setUsername] = useState("User");
  const [wano, setWano] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch balance, username, and WANO from sessionStorage
  useEffect(() => {
    const storedBalance = sessionStorage.getItem("balance");
    if (storedBalance) {
      setBalance(parseInt(storedBalance).toLocaleString());
    }

    const storedUsername = sessionStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
    }

    const storedWano = sessionStorage.getItem("wano");
    if (storedWano) {
      setWano(storedWano);
    }
  }, []);

  // Listen for balance updates dispatched by other parts of the app
  useEffect(() => {
    const onBalanceUpdated = (e) => {
      const val = e && e.detail !== undefined ? e.detail : null;
      if (val !== null) {
        try {
          setBalance(parseInt(val).toLocaleString());
        } catch (_) {
          setBalance(String(val));
        }
      }
    };

    window.addEventListener("balanceUpdated", onBalanceUpdated);
    return () => window.removeEventListener("balanceUpdated", onBalanceUpdated);
  }, []);

  const menuItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zM13 21h8v-10h-8v10zM13 3v6h8V3h-8z" fill="#4CAF50"/>
        </svg>
      ),
    },
    {
      name: "Message",
      path: "/send-message",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="#2196F3"/>
        </svg>
      ),
    },
    {
      name: "Chat",
      path: "/chat",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2z" fill="#FF9800"/>
        </svg>
      ),
    },
    {
      name: "Reports",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M3 13h2v-2H3v2zm4 0h14v-2H7v2zM3 7h18V5H3v2zm0 8h0v2h0v-2z" fill="#E91E63"/>
        </svg>
      ),
      subItems: [
        {
          name: "Message Reports",
          path: "/reports/messages",
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2V17zm4 0h-2V7h2V17zm4 0h-2v-11h2V17z" fill="#1976D2"/>
            </svg>
          ),
        },
        {
          name: "Chat Reports",
          path: "/reports/chats",
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="#9C27B0"/>
            </svg>
          ),
        },
      ],
    },
  ];

  const pathNames = {
    "/dashboard": "Dashboard",
    "/send-message": "Message",
    "/chat": "Chat",
    "/reports/messages": "Message Reports",
    "/reports/chats": "Chat Reports",
  };

  const breadcrumb = location.pathname
    .split("/")
    .filter((x) => x)
    .map((x, index, arr) => {
      const path = "/" + arr.slice(0, index + 1).join("/");
      return { name: pathNames[path] || x, path };
    });

  return (
    <>
      {/* Horizontal Title Bar - New Design */}
      <div className="horizontal-navbar">
        <div className="navbar-content">
          <div className="navbar-left">
            <img src={logo} alt="OmniChat Logo" className="navbar-logo-img" />
            <span className="navbar-title">OmniChat</span>
          </div>
          <div className="navbar-center">
            <span className="navbar-tagline">Messaging Platform</span>
          </div>
          <div className="navbar-right">
            <div className="message-balance">
              <span className="balance-label">Message Balance:</span>
              <span className="balance-value">{balance}</span>
            </div>
            <div className="navbar-user-menu" style={{ position: "relative" }}>
              <span className="user-badge" onClick={() => setShowUserMenu(!showUserMenu)} style={{ cursor: "pointer" }}>
                {username}
              </span>
              {showUserMenu && (
                <div className="user-dropdown-menu">
                  <div className="user-dropdown-header">
                    <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>{username}</p>
                    <p style={{ margin: "0 0 15px 0", fontSize: "13px", color: "#666" }}>WhatsApp: {wano}</p>
                  </div>
                  <div className="user-dropdown-qr" style={{ textAlign: "center", paddingBottom: "15px", borderBottom: "1px solid #e0e0e0" }}>
                    {wano && (
                      <>
                        <p style={{ margin: "0 0 10px 0", fontSize: "12px", color: "#666" }}>Scan to chat</p>
                        <QRCode value={`https://wa.me/${wano}?text=Hi`} size={120} />
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      sessionStorage.clear();
                      setShowUserMenu(false);
                      navigate("/login");
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      marginTop: "10px",
                      background: "#f44336",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      transition: "background 0.3s"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#d32f2f"}
                    onMouseLeave={(e) => e.target.style.background = "#f44336"}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar for desktop */}
      {!isMobile && (
        <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
          <ul className="sidebar-menu">
            {menuItems.map((item, idx) =>
              item.subItems ? (
                <li key={idx} className="sidebar-dropdown">
                  <span className="menu-item-inline">
                    <span className="menu-icon">{item.icon}</span>
                    <span>{item.name}</span>
                  </span>
                  <ul className="sidebar-submenu">
                    {item.subItems.map((sub, subIdx) => (
                      <li key={subIdx}>
                        <Link to={sub.path}>
                          <span className="menu-item-inline">
                            <span className="menu-icon">{sub.icon}</span>
                            <span>{sub.name}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={idx}>
                  <Link to={item.path}>
                    <span className="menu-item-inline">
                      <span className="menu-icon">{item.icon}</span>
                      <span>{item.name}</span>
                    </span>
                  </Link>
                </li>
              )
            )}
          </ul>
        </div>
      )}

      {/* Breadcrumb for all screens */}
      <div className="breadcrumb-wrapper">
        <div className="breadcrumb">
          {breadcrumb.length > 0 ? (
            breadcrumb.map((b, idx) => (
              <span key={b.path} className="breadcrumb-item">
                <Link
                  to={b.path}
                  style={{ color: "#fff", textDecoration: "none" }}
                >
                  {b.name}
                </Link>
                {idx < breadcrumb.length - 1 && <span className="breadcrumb-separator"> / </span>}
              </span>
            ))
          ) : (
            <span className="breadcrumb-item" style={{ color: "#fff" }}>Home</span>
          )}
        </div>
      </div>
    </>
  );
}
